import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';

const { FiZap, FiSliders, FiCheckSquare, FiSquare, FiX, FiLoader, FiCheck } = FiIcons;

const QuickGen = ({ isOpen, onClose, onGenerate }) => {
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    courseCode: '',
    totalMarks: 100,
    duration: 120,
    difficulty: 'medium',
    questionTypes: ['multiple-choice', 'short-answer', 'essay'],
    template: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState('');
  
  useEffect(() => {
    // Load templates
    try {
      const availableTemplates = storage.getTemplates();
      setTemplates(availableTemplates);
      
      // Set default template if available
      if (availableTemplates.length > 0) {
        setFormData(prev => ({ ...prev, template: availableTemplates[0].id }));
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Could not load templates. Please try again.');
    }
  }, []);
  
  if (!isOpen) return null;
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };
  
  const toggleQuestionType = (type) => {
    setFormData(prev => {
      const types = [...prev.questionTypes];
      if (types.includes(type)) {
        return { ...prev, questionTypes: types.filter(t => t !== type) };
      } else {
        return { ...prev, questionTypes: [...types, type] };
      }
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Validate form
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      
      if (!formData.subject.trim()) {
        throw new Error('Subject is required');
      }
      
      if (formData.questionTypes.length === 0) {
        throw new Error('At least one question type must be selected');
      }
      
      // Generate paper
      setTimeout(() => {
        onGenerate(formData);
        setLoading(false);
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <div className="p-6 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="bg-primary-100 dark:bg-primary-900/30 p-2 rounded-lg mr-3">
                <SafeIcon icon={FiZap} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Generate</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Create a new exam paper in seconds</p>
              </div>
            </div>
            <button 
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={onClose}
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Midterm Exam - Database Systems"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Computer Science"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Course Code
                  </label>
                  <input
                    type="text"
                    name="courseCode"
                    value={formData.courseCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., CS301"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Total Marks
                  </label>
                  <input
                    type="number"
                    name="totalMarks"
                    value={formData.totalMarks}
                    onChange={handleNumberChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    min="1"
                    max="1000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleNumberChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    min="1"
                    max="1000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Difficulty
                  </label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Template
                  </label>
                  <select
                    name="template"
                    value={formData.template}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {templates.length > 0 ? (
                      templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))
                    ) : (
                      <option value="">Default Template</option>
                    )}
                  </select>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Question Types
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => toggleQuestionType('multiple-choice')}
                      className={`px-4 py-2 rounded-lg flex items-center text-sm ${
                        formData.questionTypes.includes('multiple-choice')
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <SafeIcon 
                        icon={formData.questionTypes.includes('multiple-choice') ? FiCheckSquare : FiSquare} 
                        className="mr-1" 
                      />
                      Multiple Choice
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleQuestionType('short-answer')}
                      className={`px-4 py-2 rounded-lg flex items-center text-sm ${
                        formData.questionTypes.includes('short-answer')
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <SafeIcon 
                        icon={formData.questionTypes.includes('short-answer') ? FiCheckSquare : FiSquare} 
                        className="mr-1" 
                      />
                      Short Answer
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleQuestionType('essay')}
                      className={`px-4 py-2 rounded-lg flex items-center text-sm ${
                        formData.questionTypes.includes('essay')
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <SafeIcon 
                        icon={formData.questionTypes.includes('essay') ? FiCheckSquare : FiSquare} 
                        className="mr-1" 
                      />
                      Essay
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleQuestionType('true-false')}
                      className={`px-4 py-2 rounded-lg flex items-center text-sm ${
                        formData.questionTypes.includes('true-false')
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <SafeIcon 
                        icon={formData.questionTypes.includes('true-false') ? FiCheckSquare : FiSquare} 
                        className="mr-1" 
                      />
                      True/False
                    </button>
                  </div>
                </div>
              </div>
              
              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mr-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Generate Now
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuickGen;